<script setup>
    import { ref, computed, onMounted, watch } from 'vue';
    import { copyTextToClipboard } from '@/services/clipboard';
    import { DateTime } from 'luxon';
    import CardModal from '@/components/CardModal';
    import { usePage } from '@inertiajs/vue3';

    const couponCode = ref(null);
    const codeCopied = ref(false);
    const webshopLink = ref(null);
    const copiedMessageTimeout = ref(null);
    const codeCopiedModal = ref(null);

    const props = defineProps({
        campaign: Object,
        company: Object,
        companyImages: Object,
    });

    function copyCode() {
        return copyTextToClipboard(couponCode.value);
    }

    async function claimCouponCode () {
        return axios.get('/api/site/campaign/claimcode/' + props.campaign.id).then(response => {
            couponCode.value = response.data.data;
        }).catch(error => {
            if ([404, 500].includes(error.response.status)) {
                $('.modal').modal('hide');
                $('#serverErrorModal').modal('show');
            }

            if (error.response.data.message === 'expired') {
                $('.modal').modal('hide');
                $('#couponCodeExpiredModal').modal('show');
            }
        });
    }

    async function start() {
        const couponCodeType = props.campaign.couponCodeType.type;

        if (['general_code', 'one_code_per_member', 'first_come_first'].includes(couponCodeType)) {
            copyCode();
            codeCopiedModal.value.show();
        }

        if (couponCodeType === 'external_url') {
            window.open(couponCode.value, '_blank');
        }
    }

    function prependProtocol(url) {
        if (url && !url.startsWith('https://') && !url.startsWith('http://')) {
            return `http://${url}`;
        }

        return url;
    }

    if (props.company.webshops && props.company.webshops.length && props.company.webshops[0].url && !props.campaign.url) {
        webshopLink.value =  prependProtocol(props.company.webshops[0].url);
    }

    if (props.campaign.url) {
        webshopLink.value = prependProtocol(props.campaign.url)
    }

    const endDateFormatted = computed(() => {
        return DateTime.fromISO(props.campaign.end_date).toISODate();
    });

    defineExpose({ start });

    watch(() => usePage().props.currentUser, (currentValue, oldValue) => {
        if (currentValue !== null) {
            claimCouponCode();
        }
    });

    onMounted(() => {
        if (props.campaign.couponCodeType.type !== 'external_url' && usePage().props.currentUser !== null) {
            claimCouponCode();
        }
    });
</script>

<template>
    <div>
        <textarea
            v-if="['general_code', 'external_url'].includes(campaign.couponCodeType.type)"
            id="textareaWithCode"
            style="position: absolute;opacity: 0;z-index: -1;">
            {{ couponCode }}
        </textarea>

        <card-modal type="local" ref="codeCopiedModal" title="Yay!">
            <div v-if="['general_code', 'one_code_per_member', 'first_come_first'].includes(campaign.couponCodeType.type)">
                <h4 style="font-family: AvertaBold;" class="mt-2">Koden är kopierad.</h4>

                <h3 class="mt-4">Nu är det bara att klistra in den i kassan!</h3>
            </div>

            <a :href="webshopLink" class="btn btn-outline-danger mb-4" style="height: auto; padding-top: 15px; padding-bottom: 15px; margin-top: 35px;" target="_blank" :rel="`${company.follow_links ? '' : 'nofollow' } external`">Ta del av erbjudandet</a>
        </card-modal>

       <div class="modal fade" id="localGeneralCodeModal" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true" ref="couponCodeContainer">
            <div class="modal-dialog coupon-modal" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div class="logo-rounded mt-0" style="margin:0 auto 0 auto;">
                        <a :href="campaign.url ? campaign.url : 'javascript:void(0)'" :target="campaign.url ? '_blank' : null" :rel="`${company.follow_links ? '' : 'nofollow' } external`">
                            <img :src="companyImages.company_logo_image" :alt="`${company.name} Logga`">
                        </a>
                    </div>

                    <p class="discount-text" style="color: #444; font-family: AvertaLight;">{{ campaign.heading }}</p>

                    <a target="_blank" :href="couponCode" v-if="campaign.couponCodeType.type === 'external_url'">
                        <button class="btn btn-danger w-100 my-2">
                            Använd nu!
                        </button>
                    </a>

                    <!-- <template v-if="campaign.couponCodeType.type === 'general_code'">
                        <textarea
                            v-if="campaign.couponCodeType.type === 'general_code'"
                            id="textareaWithCode" style="position: absolute;opacity: 0;z-index: -1;">{{ couponCode }}</textarea>
                        <input class="code-view" type="text" ref="codeInput" :disabled="true" :value="couponCode">
                        <button class="btn btn-danger w-100 my-2" @click="copyCode">Kopiera kod</button>
                        <p v-if="codeCopied" class="code-copied">Koden har kopierats till urklipp.</p>
                    </template> -->

                    <template v-if="['one_code_per_member', 'first_come_first'].includes(campaign.couponCodeType.type)">
                        <textarea id="textareaWithCode" style="position: absolute;opacity: 0;z-index: -1;">{{ couponCode }}</textarea>

                        <img src="/assets/frontend/coupon_code_mask.png" v-if="couponCode === null">
                        <button class="btn btn-danger w-100 my-2" @click="claimCouponCode" v-if="couponCode === null">Använd nu!*</button>

                        <input class="code-view" type="text" :disabled="true" v-if="couponCode !== null" :value="couponCode">
                        <button class="btn btn-danger w-100 my-2" @click="copyCode" v-if="couponCode !== null">Kopiera kod</button>
                        <p v-if="codeCopied" class="code-copied">Koden har kopierats till urklipp.</p>

                        <div class="offer red  small" v-if="couponCode === null">
                            <p><span>*Aktiverad kod är giltig i <b>24 timmar</b></span></p>
                        </div>

                        <div class="offer small" v-if="couponCode !== null" style="color: #444;">
                            <p><span>Koden är giltig i <b>24 timmar</b> efter aktivering</span></p>
                        </div>
                    </template>

                    <div v-if="campaign.end_date" class="offer small" style="color: #444; font-size: 14px;">
                        <p class="m-0"><span class="text-bold" >{{ $t('limited_time_offer') }}</span></p>
                        <p><span>{{ $t('valid_until') }} {{endDateFormatted}}</span></p>
                    </div>

                    <div class="offer red  small mt-auto" v-if="webshopLink">
                        <a :href="webshopLink" target="_blank" :rel="`${company.follow_links ? '' : 'nofollow' } external`">
                        <p><span>Ta mig till <br><b style="font-size: 27px;">{{company.name}}</b></span></p>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

